import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { TaskCatalogApiService } from '../../../services/task-catalog-api.service';
import { TaskStatusRequest, TaskStatusResponse } from '../../../models/task.model';
import { slugifyToCode } from '../../../utils/slugify-code.util';
import { ToastService } from '../../../../../shared/services/toast.service';

@Component({
  selector: 'app-task-statuses-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './task-statuses-form.component.html',
})
export class TaskStatusesFormComponent implements OnInit {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TaskStatusResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;
  isSystem = false;

  private nameManuallyEdited = false;

  readonly COLOR_PRESETS = [
    '#FFC107', '#F59E0B', '#EF4444', '#F43F5E',
    '#84CC16', '#22C55E', '#10B981', '#14B8A6',
    '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
    '#A855F7', '#EC4899', '#6B7280', '#1F2937',
  ];

  constructor(private fb: FormBuilder, private api: TaskCatalogApiService, private toast: ToastService) {
    this.initForm();
  }

  ngOnInit(): void {}

  // displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      label: ['', [Validators.required, Validators.maxLength(100)]],
      color: ['#FFC107', [Validators.maxLength(30)]],
      isFinal: [false],
      isCompletedTerminal: [false],
      isCancelledTerminal: [false],
      isDefault: [false],
      isActive: [true],
    });
  }

  /**
   * Mantiene la consistencia entre `isFinal`, `isCompletedTerminal` e `isCancelledTerminal`.
   * Reglas del backend: cada terminal requiere `isFinal=true` y son mutuamente excluyentes.
   */
  onTerminalToggle(field: 'isCompletedTerminal' | 'isCancelledTerminal', checked: boolean): void {
    const other = field === 'isCompletedTerminal' ? 'isCancelledTerminal' : 'isCompletedTerminal';
    if (checked) {
      this.form.patchValue({ [field]: true, [other]: false, isFinal: true });
    } else {
      this.form.patchValue({ [field]: false });
    }
  }

  /** Si se desmarca `isFinal`, también se limpian las marcas de terminal (no son válidas sin él). */
  onIsFinalToggle(checked: boolean): void {
    if (!checked) {
      this.form.patchValue({ isFinal: false, isCompletedTerminal: false, isCancelledTerminal: false });
    } else {
      this.form.patchValue({ isFinal: true });
    }
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.isSystem = false;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.nameManuallyEdited = false;
    this.initForm();
  }

  loadItem(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.itemId = id;
    this.nameManuallyEdited = true;
    this.api.getStatusById(id).subscribe({
      next: (item) => {
        this.isSystem = !!item.isSystem;
        this.form.patchValue({
          name: item.name,
          label: item.label,
          color: item.color ?? '#FFC107',
          isFinal: !!item.isFinal,
          isCompletedTerminal: !!item.isCompletedTerminal,
          isCancelledTerminal: !!item.isCancelledTerminal,
          isDefault: !!item.isDefault,
          isActive: item.isActive ?? true,
        });
        if (this.isSystem) this.form.get('name')?.disable();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el estado';
        this.loading = false;
      },
    });
  }

  onLabelInput(value: string): void {
    if (!this.nameManuallyEdited) {
      this.form.patchValue({ name: slugifyToCode(value) }, { emitEvent: false });
    }
  }

  onNameInput(): void { this.nameManuallyEdited = true; }

  setColor(c: string): void { this.form.patchValue({ color: c }); }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = '';

    const v = this.form.getRawValue();
    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const body: TaskStatusRequest = {
      name: v.name.trim().toUpperCase(),
      label: v.label.trim(),
      color: v.color || undefined,
      isFinal: !!v.isFinal,
      isCompletedTerminal: !!v.isCompletedTerminal,
      isCancelledTerminal: !!v.isCancelledTerminal,
      isDefault: !!v.isDefault,
      isActive: v.isActive ?? true,
    };

    const req$ = this.isEditing && this.itemId
      ? this.api.updateStatus(this.itemId, body)
      : this.api.createStatus(body);

    req$.subscribe({
      next: (item) => { this.saving = false; this.saved.emit(item); },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el estado de tarea';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  cancelar(): void { this.cancelled.emit(); }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
